export const CyberpunkParticle = ({
  x,
  y,
  delay,
  type,
}: {
  x: number;
  y: number;
  delay: number;
  type: string;
}) => {
  const getParticleStyle = () => {
    switch (type) {
      case 'matrix':
        return 'w-4 h-4 bg-gradient-to-r from-green-400 to-emerald-500 text-green-300 text-xs font-mono flex items-center justify-center animate-pulse';
      case 'digital':
        return 'w-3 h-3 bg-gradient-to-r from-cyan-400 to-blue-500 animate-ping shadow-lg shadow-cyan-400/50';
      case 'neon':
        return 'w-2 h-2 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full animate-bounce shadow-lg shadow-pink-400/50';
      case 'circuit':
        return 'w-4 h-1 bg-gradient-to-r from-yellow-400 to-orange-500 animate-pulse shadow-lg shadow-yellow-400/50';
      case 'hologram':
        return 'w-3 h-3 bg-gradient-to-r from-indigo-400 to-purple-500 animate-spin shadow-lg shadow-indigo-400/50';
      case 'data':
        return 'w-2 h-4 bg-gradient-to-b from-cyan-300 to-blue-600 animate-pulse shadow-lg shadow-cyan-300/50';
      default:
        return 'w-2 h-2 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full animate-ping';
    }
  };

  const getContent = () => {
    if (type === 'matrix') {
      const chars = ['0', '1', 'ア', 'カ', 'サ', 'タ', 'ナ', 'ハ', 'マ', 'ヤ', 'ラ', 'ワ'];
      return chars[Math.floor(Math.random() * chars.length)];
    }
    return '';
  };

  return (
    <div
      className={`absolute ${getParticleStyle()}`}
      style={{
        left: `${x}px`,
        top: `${y}px`,
        animationDelay: `${delay}ms`,
        animationDuration: type === 'circuit' ? '3s' : type === 'matrix' ? '2s' : '1.5s',
        transform: `rotate(${Math.random() * 360}deg)`,
        clipPath: type === 'hologram' ? 'polygon(50% 0%, 0% 100%, 100% 100%)' : 'none',
      }}
    >
      {getContent()}
    </div>
  );
};

export const CyberpunkExplosion = ({ delay }: { delay: number }) => {
  return (
    <div
      className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 border-4 border-cyan-400 animate-ping opacity-70 shadow-2xl shadow-cyan-400/50"
      style={{
        width: '350px',
        height: '350px',
        animationDelay: `${delay}ms`,
        animationDuration: '2.5s',
        clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)',
        background: 'linear-gradient(45deg, rgba(0,255,255,0.3), rgba(128,0,255,0.3))',
      }}
    />
  );
};

export const DigitalGlitch = ({ delay }: { delay: number }) => {
  return (
    <div
      className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
      style={{
        animationDelay: `${delay}ms`,
      }}
    >
      {/* Scan lines */}
      <div
        className="absolute w-96 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent animate-pulse opacity-80"
        style={{ top: '-50px', animationDuration: '0.5s' }}
      />
      <div
        className="absolute w-96 h-1 bg-gradient-to-r from-transparent via-purple-400 to-transparent animate-pulse opacity-80"
        style={{ top: '50px', animationDuration: '0.7s' }}
      />

      {/* Glitch blocks */}
      <div
        className="absolute w-20 h-4 bg-cyan-400 opacity-60 animate-pulse"
        style={{ left: '-100px', top: '-20px', animationDuration: '0.3s' }}
      />
      <div
        className="absolute w-16 h-3 bg-purple-400 opacity-60 animate-pulse"
        style={{ right: '-80px', top: '10px', animationDuration: '0.4s' }}
      />
    </div>
  );
};

export const MatrixRain = ({ delay }: { delay: number }) => {
  return (
    <div
      className="absolute top-0 left-1/2 transform -translate-x-1/2 pointer-events-none"
      style={{
        animationDelay: `${delay}ms`,
      }}
    >
      {Array.from({ length: 8 }, (_, i) => (
        <div
          key={i}
          className="absolute w-1 bg-gradient-to-b from-green-400 via-green-300 to-transparent animate-pulse"
          style={{
            left: `${(i - 4) * 20}px`,
            height: '200px',
            animationDuration: `${1 + Math.random()}s`,
            animationDelay: `${Math.random() * 1000}ms`,
          }}
        />
      ))}
    </div>
  );
};

export const DigitalWave = ({ delay }: { delay: number }) => {
  return (
    <div
      className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 border-2 border-purple-400 animate-ping opacity-50"
      style={{
        width: '400px',
        height: '400px',
        animationDelay: `${delay}ms`,
        animationDuration: '3s',
        clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
        background: 'linear-gradient(135deg, rgba(255,0,255,0.2), rgba(0,255,255,0.2))',
      }}
    />
  );
};
